import { useEffect, useState } from 'react';

import { EnvVars } from '@/../environment';
import { useParams } from 'react-router-dom';
import { Tldraw, Editor, loadSnapshot, DefaultFontStyle, TLEditorSnapshot } from 'tldraw';

import { ViewMode } from '../view-selector';
import { ViewSelector } from '../view-selector';
import { AddInBetweenNodeButton } from './components/TimelineElements/AddInBetweenNodeButton';
import { AddNewNodeButton } from './components/TimelineElements/AddNewNodeButton';
import { VisualChronologyNode } from './components/TimelineElements/VisualChronologyNode';
import { YearMarkerNode } from './components/TimelineElements/YearMarkerNode';
import { ClearButton } from './components/UIElements/ClearButton';
import { ExportButton } from './components/UIElements/ExportButton';
import { SaveButton } from './components/UIElements/SaveButton';
import { SearchBar } from './components/UIElements/SearchBar';
import { useTimelineChanges } from './hooks/useTimelineChanges';
import { overrides, handleShapesDeletion, createTimeline } from './timelineDrawingHelpers';
import { useGetChronologyVisualisation } from '@/api/queries/useGetChronologyVisualisation';
import { Icon } from '@/new/components/icon';
import { ChronologyFact } from '@/types';

import 'tldraw/tldraw.css';

export const VisualTimeline = ({
  chronology,
  viewMode,
  onViewModeChange,
}: {
  chronology: ChronologyFact[];
  viewMode: ViewMode;
  onViewModeChange: (viewMode: ViewMode) => void;
}) => {
  const { chronologyId, matterId, subsetId } = useParams();
  if (!chronologyId || !matterId || !subsetId) return null;

  const [editor, setEditor] = useState<Editor | null>(null);

  // Get the startTracking method from useTimelineChanges
  const timelineChanges = useTimelineChanges({
    editor,
    caseId: subsetId,
    chronologyId,
  });

  const {
    data: savedVisualisation,
    isLoading: isLoadingVisualisation,
    isFetching: isFetchingVisualisation,
  } = useGetChronologyVisualisation({
    caseId: subsetId,
    chronologyId,
  });

  DefaultFontStyle.setDefaultValue('sans');

  const loadTimeline = (editor: Editor, snapshot: TLEditorSnapshot) => {
    loadSnapshot(editor.store, {
      session: snapshot.session,
      document: snapshot.document,
    });
  };

  useEffect(() => {
    if (!editor) return;

    // Listen for any store changes and handle shape deletions
    const unsubscribe = editor.store.listen((event) => {
      if (event.changes && event.changes.removed && Object.keys(event.changes.removed).length > 0) {
        // Get the IDs of all the deleted nodes
        const deletedShapeIds = Object.keys(event.changes.removed);

        // Check if any deleted shapes are node types and if so run deletion helper function
        const isNode = deletedShapeIds.some((s) => s.startsWith('shape:visual-chronology-node-'));
        if (isNode) {
          handleShapesDeletion(editor, deletedShapeIds).catch(console.error);
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [editor]);

  return (
    <>
      {isLoadingVisualisation || isFetchingVisualisation ? (
        <div className="flex h-full w-full items-center justify-center">
          <Icon className="iconify lucide--loader-circle h-4 w-4 animate-spin duration-700" />
        </div>
      ) : (
        <>
          {chronology.length > 500 ? (
            <div className="flex h-full w-full items-center justify-center bg-gray-50">
              <div className="mx-auto max-w-md text-center">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
                  <Icon className="iconify lucide--alert-triangle h-8 w-8 text-amber-600" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-gray-900">Chronology Too Large</h3>
                <p className="mb-6 text-sm leading-relaxed text-gray-600">
                  This chronology contains {chronology.length} facts, which exceeds the visual timeline's capacity of
                  500 facts. For better performance and, please use the table view instead.
                </p>
              </div>
            </div>
          ) : (
            <Tldraw
              licenseKey={EnvVars.TLDRAW_LICENSE_KEY}
              shapeUtils={[VisualChronologyNode, YearMarkerNode, AddNewNodeButton, AddInBetweenNodeButton]}
              components={{
                PageMenu: null,
                StylePanel: null,
                TopPanel: null,
                MainMenu: null,
                MenuPanel: null,
                Minimap: null,
                ZoomMenu: null,
                ContextMenu: null,
                CursorChatBubble: null,
                HelpMenu: null,
                KeyboardShortcutsDialog: null,
                QuickActions: null,
                ActionsMenu: null,
                HelperButtons: null,
              }}
              overrides={overrides}
              onMount={(editor) => {
                setEditor(editor);
                if (savedVisualisation?.snapshot) {
                  loadTimeline(editor, savedVisualisation.snapshot);
                } else if (chronology) {
                  createTimeline(editor, chronology);
                }

                // Start tracking changes after timeline is created
                // Unfortunately even though TLDraw creates canvas elements
                // synchronously, it doesn't return promises for these updates
                setTimeout(() => {
                  timelineChanges?.startTracking();
                }, 1000);
              }}
            >
              <div className="absolute top-2 left-2 rounded-lg border border-gray-100 bg-white/90 px-4 py-2 shadow-lg">
                <SearchBar />
              </div>

              <div className="absolute top-2 right-56 flex items-center gap-2 rounded-lg border border-gray-100 bg-white/90 px-4 py-2 shadow-lg">
                <ExportButton />
                <span className="mx-2 h-6 w-px bg-gray-200" />
                <ClearButton />
                <span className="mx-2 h-6 w-px bg-gray-200" />
                <SaveButton
                  caseId={subsetId}
                  chronologyId={chronologyId}
                  chronology={chronology}
                  originalSnapshot={savedVisualisation?.snapshot}
                  timelineChanges={timelineChanges}
                />
              </div>
              <div className="absolute top-2 right-2 rounded-lg border border-gray-100 bg-white/90 px-4 py-2.25 shadow-lg">
                <ViewSelector viewMode={viewMode} onViewModeChange={onViewModeChange} />
              </div>
            </Tldraw>
          )}
        </>
      )}
    </>
  );
};
